import { Injectable } from '@angular/core';
import { from, Observable, Subscription } from 'rxjs';
import { TokenJWT } from 'src/app/core/models/token/token-jwt';
import { TokenService } from 'src/app/core/services/token/token.service';
import { environment } from 'src/environments/environment';
import { GdprConsent } from '../../models/gdpr';
import { ToastController } from '@ionic/angular';

@Injectable({
  providedIn: 'root'
})
export class GdprDataService {
  private token: TokenJWT | undefined; // informazioni dell'account
  private tokenSubscription$: Subscription = new Subscription();

  constructor(
    private tokenService: TokenService,
    private toastController: ToastController
  ) { 
    this.getToken();
  }

  ngOnDestroy(): void {
    if (this.tokenSubscription$) this.tokenSubscription$.unsubscribe();
  }

  private getToken(): void {
    this.tokenSubscription$ = this.tokenService
      .getToken()
      .subscribe((token) => {
        this.token = token;
      });
  }

  private waitForToken(timeoutMs = 3000): Promise<void> {
    return new Promise((resolve, reject) => {
      const interval = 50;
      let waited = 0;

      const check = () => {
        if (this.token) {
          resolve();
        } else if (waited >= timeoutMs) {
          reject(new Error('Token non disponibile entro il tempo limite'));
        } else {
          waited += interval;
          setTimeout(check, interval);
        }
      };

      check();
    });
  }

  //#region GET

  /**
   * Richiede al server i consensi dell'area richiesta
   */
  public async getConsents(idArea: number): Promise<GdprConsent[]>{
    try {
      const params = new URLSearchParams({ IDArea: idArea.toString() });
      
      const response = await fetch(
        environment.apiUrl +
        environment.apiVersion +
        '/gdpr/read' +
        '?' +
        params,
        {
          headers: {},
        }
      );

      let data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body as GdprConsent[];
      
    } catch (error) {
      console.error(error);
      Promise.resolve(false);

      return [];
    }
  }

  /**
   * Richiede al server i consensi dell'account
   */
  public async getAccountConsents(): Promise<Array<string|number>>{
    try {
      await this.waitForToken(); //aspetto che il token venga settato per effettuare il controllo

      const response = await fetch(
        environment.apiUrl +
        environment.apiVersion +
        '/accounts/gdpr/read',
        {
          headers: { Authorization: this.token!.toString() },
        }
      );

      let data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body;
      
    } catch (error) {
      console.error(error);
      Promise.resolve(false);

      return [];
    }
  }

  /**
   * Richiede al server la validità dei consensi accettati di un account relativi ad un'area
   * @param idArea
   * 
   * @returns boolean
   */
  public async getAccountAreaValidation(idArea: number): Promise<boolean>{
    try {
      await this.waitForToken(); //aspetto che il token venga settato per effettuare il controllo

      const params = new URLSearchParams({ IDArea: idArea.toString() });

      const response = await fetch(
        environment.apiUrl +
        environment.apiVersion +
        '/accounts/gdpr/check-validation/read' +
        '?' +
        params,
        {
          headers: { Authorization: this.token!.toString() },
        }
      );

      let data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return data.body.valido;
      
    } catch (error) {
      console.error(error);
      return true;
    }
  }

  /**
   * Restituisce un observable della chiamata getAccountAreaValidation
   * @param idArea
   * 
   * @returns boolean
   */
  public getAccountAreaValidation$(idArea: number): Observable<boolean> {
    return from(this.getAccountAreaValidation(idArea));
  }

  //#endregion

  //#region POST

  /**
   * Aggiunge i consensi accettati all'account
   * @param consensi array idVersione e stato
   */
  public async saveConsents(consensi: {ID: string, Accettato: boolean}[]){
    const params = JSON.stringify({
      Consensi: consensi
    });

    try {
      const response = await fetch(
        environment.apiUrl + 
        environment.apiVersion + 
        '/accounts/gdpr/create',
        {
          headers: { Authorization: this.token!.toString() },
          method: 'POST',
          body: params,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      return true;

    } catch (error) {

      console.error('Error modifica consensi', error);
      const toast = await this.toastController.create({
        message: 'Ops, qualcosa è andato storto!',
        duration: 2000,
        position: 'top',
        cssClass: 'custom-toast',
      });
      await toast.present();

      return false;
    }
  }

  //#endregion
}
